import * as React from "react"
import { cn } from "@/lib/utils"

export interface HelpIconProps extends React.SVGProps<SVGSVGElement> {
  /**
   * Size of the icon in pixels
   * @default 18
   */
  size?: number
  /**
   * Whether the icon is in active/clicked state
   */
  active?: boolean
}

/**
 * Help icon as SVG (info/question mark symbol)
 *
 * This icon represents help/information in the ANYW3AR app.
 * Typically used with a tooltip to provide contextual help to users.
 *
 * Design specs:
 * - Interactive: cursor pointer with hover states
 * - Color: gray-500 default, gray-700 hover, gray-900 active
 * - Active state: text-gray-900
 *
 * @example
 * ```tsx
 * <HelpIcon />
 * <HelpIcon size={16} className="text-gray-500" />
 * <HelpIcon active={isActive} onClick={handleClick} />
 * ```
 */
export const HelpIcon = React.forwardRef<SVGSVGElement, HelpIconProps>(
  ({ size = 18, active, className, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        width={size}
        height={size}
        viewBox="0 0 12.4 12.4"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        className={cn(
          "inline-flex shrink-0 cursor-pointer select-none text-gray-500 hover:text-gray-700 active:text-gray-900 transition-colors",
          active && "text-gray-900",
          className
        )}
        aria-hidden="true"
        {...props}
      >
        <path d="M6.2,12.4c-.8,0-1.6-.2-2.4-.5-.7-.3-1.4-.8-2-1.3-.6-.6-1-1.2-1.3-2-.3-.7-.5-1.5-.5-2.4s.2-1.6.5-2.4c.3-.7.8-1.4,1.3-2,.6-.6,1.2-1,2-1.3C4.5.2,5.3,0,6.2,0s1.6.2,2.4.5c.8.3,1.4.8,2,1.3.6.6,1,1.2,1.3,2,.3.7.5,1.5.5,2.4s-.2,1.7-.5,2.4c-.3.7-.8,1.4-1.3,2-.6.6-1.2,1-2,1.3-.7.3-1.5.5-2.4.5ZM5.1,9.5h2.5c.2,0,.3,0,.4-.2.1-.1.2-.2.2-.4s0-.3-.2-.4c-.1-.1-.2-.2-.4-.2h-.6v-2.5c0-.2,0-.4-.2-.5-.1-.1-.3-.2-.5-.2h-1.2c-.2,0-.3,0-.4.2-.1.1-.2.2-.2.4s0,.3.2.4c.1.1.2.2.4.2h.6v2.2h-.7c-.2,0-.3,0-.4.2-.1.1-.2.2-.2.4s0,.3.2.4c.1.1.2.2.4.2ZM6.2,4.3c.2,0,.5,0,.6-.3.2-.2.3-.4.3-.6s0-.5-.3-.7c-.2-.2-.4-.3-.6-.3s-.5,0-.6.3c-.2.2-.3.4-.3.7s0,.5.3.6c.2.2.4.3.6.3Z" />
      </svg>
    )
  }
)

HelpIcon.displayName = "HelpIcon"
